/**
 * Calcular tiempo transcurrido desde una fecha
 * @param {Date|string} date - Fecha a comparar
 * @param {string} lang - Idioma (en, id, es)
 * @returns {string} - "Hace 5 minutos", "2 horas", etc.
 */
export const timeAgo = (date, lang = 'es') => {
  if (!date) return '';
  
  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  const translations = {
    en: {
      justNow: 'Just now',
      seconds: 's ago',
      minute: '1 min ago',
      minutes: 'm ago',
      hour: '1h ago',
      hours: 'h ago',
      day: '1d ago',
      days: 'd ago',
      month: '1mo ago',
      months: 'mo ago',
      year: '1y ago',
      years: 'y ago'
    },
    id: {
      justNow: 'Baru saja',
      seconds: 'detik lalu',
      minute: '1 menit lalu',
      minutes: 'menit lalu',
      hour: '1 jam lalu',
      hours: 'jam lalu',
      day: '1 hari lalu',
      days: 'hari lalu',
      month: '1 bulan lalu',
      months: 'bulan lalu',
      year: '1 tahun lalu',
      years: 'tahun lalu'
    },
    es: {
      justNow: 'Ahora',
      seconds: 's',
      minute: '1 min',
      minutes: 'min',
      hour: '1h',
      hours: 'h',
      day: '1d',
      days: 'd',
      month: '1 mes',
      months: 'meses',
      year: '1 año',
      years: 'años'
    }
  };

  const t = translations[lang] || translations.es;

  if (diffSecs < 10) return t.justNow;
  if (diffSecs < 60) return `${diffSecs}${t.seconds}`;
  if (diffMins === 1) return t.minute;
  if (diffMins < 60) return `${diffMins}${t.minutes}`;
  if (diffHours === 1) return t.hour;
  if (diffHours < 24) return `${diffHours}${t.hours}`;
  if (diffDays === 1) return t.day;
  if (diffDays < 30) return `${diffDays}${t.days}`;
  if (diffMonths === 1) return t.month;
  if (diffMonths < 12) return `${diffMonths}${t.months}`;
  if (diffYears === 1) return t.year;
  return `${diffYears}${t.years}`;
};

/**
 * Verificar si un usuario está online
 * @param {boolean} isOnline - Estado online del usuario
 * @param {Date|string} lastSeen - Última vez visto
 * @returns {boolean} - true si está online o visto hace menos de 5 minutos
 */
export const isUserActive = (isOnline, lastSeen) => {
  if (isOnline) return true;
  if (!lastSeen) return false;
  
  const now = new Date();
  const last = new Date(lastSeen);
  const diffMs = now - last;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  
  return diffMins < 5; // Consideramos activo si se vio hace menos de 5 minutos
};

/**
 * Obtener el estado del usuario
 * @param {boolean} isOnline - Estado online del usuario
 * @param {Date|string} lastSeen - Última vez visto
 * @param {string} lang - Idioma
 * @returns {object} - { status: 'online'|'away'|'offline', text: 'Online', color: '#4ade80' }
 */
export const getUserStatus = (isOnline, lastSeen, lang = 'es') => {
  const translations = {
    en: {
      online: 'Online',
      away: 'Away',
      offline: 'Offline',
      lastSeen: 'Last seen'
    },
    id: {
      online: 'Online',
      away: 'Jauh',
      offline: 'Offline',
      lastSeen: 'Terakhir dilihat'
    },
    es: {
      online: 'En línea',
      away: 'Ausente',
      offline: 'Desconectado',
      lastSeen: 'Visto'
    }
  };

  const t = translations[lang] || translations.es;

  // `isOnline` puede venir de la columna `Users.is_online`, que se queda
  // pegada en `true` si el socket no llega a desconectarse limpiamente.
  // Si `last_seen` es antiguo mostramos la última actividad en vez de un
  // "Online" falso. Sin `last_seen` confiamos en el valor recibido (presencia
  // en vivo por Socket.IO, p. ej. en Messages).
  const onlineIsCredible = isOnline && (!lastSeen || isUserActive(false, lastSeen));

  if (onlineIsCredible) {
    return {
      status: 'online',
      text: t.online,
      color: '#4ade80', // green-400
      dot: true
    };
  }

  if (isUserActive(false, lastSeen)) {
    return {
      status: 'away',
      text: `${t.lastSeen} ${timeAgo(lastSeen, lang)}`,
      color: '#fbbf24', // amber-400
      dot: false
    };
  }

  return {
    status: 'offline',
    text: lastSeen ? `${t.lastSeen} ${timeAgo(lastSeen, lang)}` : t.offline,
    color: '#9ca3af', // gray-400
    dot: false
  };
};

const TimeUtils = {
  timeAgo,
  isUserActive,
  getUserStatus
};

export default TimeUtils;
